import React, { useEffect, useState } from "react";
import { Section } from "./Section";
import useDebounce from "../../hooks/useDebounce";

import { Material, Price } from "./types";
import { Dimensions, Units, Dimension } from "../Dimensions";
import { IShape } from "./ShapeChooser";
import interpolate, { trPricingCalculator } from "../../util/interpolate";
import Footer, { SubmitType } from "./sections/Footer";
import PricesSection from "./sections/Prices";
import MaterialsSection from "./sections/Materials";
import FullScreenDialog from "./FullScreenDialog";
import type { Translations } from "../../types";
import { Box } from "@mui/material";
import ShapeSection from "./sections/Shapes";
import { DimensionsSelect } from "../Dimensions/DimensionsSelect";

const buttonsOptions: {
  title: string;
  width: number;
  height: number;
  units: Units;
}[] = [
  { title: `25mm (1")`, width: 25, height: 25, units: "mm" },
  { title: `32mm (1.25")`, width: 32, height: 32, units: "mm" },
  { title: `37mm (1.5")`, width: 37, height: 37, units: "mm" },
  { title: `58mm (2.25")`, width: 58, height: 58, units: "mm" },
];

export interface IProps {
  currencyCode: string;
  locale: string;

  defaultMaterialSku: string;
  materials: Material[];

  shape: string;
  shapes?: IShape[] | null;

  price: Price | null;
  prices: Price[] | null;
  pricesIncTax: boolean;
  moq: number;

  onClickMaterial?: () => void;
  onClickShape?: () => void;
  onChangeComplete: (
    width: number,
    height: number,
    quantity: number,
    materialSku: string,
    shape: string,
    units: Units
  ) => void;
  onSubmit: (stype: SubmitType) => void;

  units: Units;

  minDimensions: Dimension;
  maxDimensions: Dimension;

  quantity: number;
  width: number;
  height: number;

  isSample?: boolean;

  allTranslations: Translations;

  dimensionsEnabled?: boolean;
  materialEnabled?: boolean;
  shapeEnabled?: boolean;
  quantityEnabled?: boolean;
  fixedQuantity?: boolean;
  showInches?: boolean;
  productSku?: string;
  fixedAspectRatio?: boolean;
  graphicMode?: boolean;
}

export const VerticalPricingCalculator = (props: IProps) => {
  const { PricingCalculator: translations } = props.allTranslations;
  const [quantity, setQuantity] = useState(props.quantity.toString());
  const [width, setWidth] = useState(props.width);
  const [height, setHeight] = useState(props.height);
  const quantityNum = parseInt(quantity);
  const [units, setUnits] = useState(props.units);
  const [materialSku, setMaterialSku] = useState(props.defaultMaterialSku);
  const [shape, setShape] = useState(props.shape);
  const [changed, setChanged] = useState({
    width,
    height,
    quantity: quantityNum,
    units,
    materialSku,
    shape,
  });
  const debouncedChanged = useDebounce(changed, 100);
  const [view, setView] = useState<"pc" | "materials" | "shapes">("pc");

  useEffect(() => {
    setMaterialSku(props.defaultMaterialSku);
    setShape(props.shape);
  }, [props.defaultMaterialSku, props.shape]);

  useEffect(() => {
    const { width, height, quantity, materialSku, units } = debouncedChanged;

    if (isNaN(width) || isNaN(height) || isNaN(quantity)) {
      return;
    }

    props.onChangeComplete(width, height, quantity, materialSku, shape, units);
  }, [debouncedChanged]);

  const tr = interpolate({
    ...translations,
    cm: props.allTranslations.Dimensions.cm,
    in: props.allTranslations.Dimensions.in,
  });

  const t = trPricingCalculator(tr, {
    units: units,
    width: width,
    height: height,
    moq: props.moq,
    minDimensions: props.minDimensions,
    maxDimensions: props.maxDimensions,
  });

  function handleCustomPricing(price: Price) {
    setQuantity(price.quantity.toString());
    setChanged((prev) => {
      return {
        ...prev,
        quantity: price.quantity,
      };
    });
  }

  function handleSetMaterial(materialSku: string) {
    setMaterialSku(materialSku);
    setChanged((prev) => {
      return {
        ...prev,
        materialSku,
      };
    });
    setView("pc");
  }

  function handleSetShape(shape: string) {
    setShape(shape);
    setChanged((prev) => {
      return {
        ...prev,
        shape,
      };
    });
    setView("pc");
  }

  function handleCustomQuantity(
    ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) {
    setQuantity(ev.target.value);
    setChanged((prev) => {
      return {
        ...prev,
        quantity: +ev.target.value,
      };
    });
  }

  const quantityError = props.price === null || quantityNum < props.moq;
  const isButton =
    props.productSku?.startsWith("BUTTONS-") ||
    props.productSku?.includes("SAMPLE-BUTTONS");
  const usesDimensionsOptions = isButton;

  if (view === "materials") {
    return (
      <FullScreenDialog
        type="material"
        materials={props.materials}
        locale={props.locale}
        materialSku={materialSku}
        design={{
          productSku: props.productSku,
          quantity: quantityNum,
          width,
          height,
          unit: units,
          materialSku,
          shape,
        }}
        price={props.price}
        showPricesIncTax={props.pricesIncTax}
        onChangeConfirmed={handleSetMaterial}
        onCancel={() => setView("pc")}
        translations={props.allTranslations.MaterialChooser}
      />
    );
  } else if (view === "shapes") {
    return (
      <FullScreenDialog
        type="shape"
        shape={shape}
        // locale={props.locale}
        shapes={props.shapes || []}
        onChangeConfirmed={handleSetShape}
        onCancel={() => setView("pc")}
        translations={{
          ...props.allTranslations.ShapeChooser,
          shapes: props.allTranslations.Shapes,
        }}
      />
    );
  }

  return (
    <>
      {props.dimensionsEnabled ? (
        <Box
          sx={{
            paddingLeft: { xs: "14px", md: "26px" },
            paddingRight: { xs: "14px", md: "26px" },
          }}
        >
          <Section
            key="size"
            number={1}
            title={t(isButton ? "chooseSize" : "enterSize")}
            subtitle={isButton ? undefined : t("enterSizeDesc")}
            data-section="Size"
            sx={{ px: { md: "23px" } }}
          >
            {usesDimensionsOptions ? (
              <DimensionsSelect
                width={props.width}
                height={props.height}
                units={props.units}
                displayAs={"mm"}
                options={buttonsOptions}
                onChangeConfirmed={(newWidth, newHeight, newUnits) => {
                  setWidth(newWidth);
                  setHeight(newHeight);
                  setUnits(newUnits);
                  setChanged((prev) => {
                    return {
                      ...prev,
                      width: newWidth,
                      height: newHeight,
                      units: newUnits,
                    };
                  });
                }}
                showInches={props.showInches}
                translations={props.allTranslations.Dimensions}
              />
            ) : (
              <Dimensions
                width={props.width}
                height={props.height}
                units={props.units}
                displayAs={props.units}
                minDimensions={props.minDimensions}
                maxDimensions={props.maxDimensions}
                fixedAspectRatio={props.fixedAspectRatio}
                onChangeConfirmed={(newWidth, newHeight, newUnits) => {
                  setWidth(newWidth);
                  setHeight(newHeight);
                  setUnits(newUnits);
                  setChanged((prev) => {
                    return {
                      ...prev,
                      width: newWidth,
                      height: newHeight,
                      units: newUnits,
                    };
                  });
                }}
                showInches={props.showInches}
                translations={props.allTranslations.Dimensions}
              />
            )}
          </Section>
        </Box>
      ) : null}

      <Box
        sx={{
          paddingLeft: { xs: "14px", md: "26px" },
          paddingRight: { xs: "14px", md: "26px" },
        }}
      >
        {props.materialEnabled && props.materials.length > 1 ? (
          <MaterialsSection
            materials={props.materials || []}
            materialSku={materialSku || props.defaultMaterialSku}
            translations={{
              materialLabel: translations.materialLabel,
              selectYourMaterial: translations.selectYourMaterial,
              bestseller: translations.bestseller,
            }}
            onChangeComplete={(materialSku) => {
              setMaterialSku(materialSku);
              setChanged((prev) => {
                return {
                  ...prev,
                  materialSku,
                };
              });
            }}
            onOpenMaterials={() => {
              setView("materials");
            }}
          />
        ) : null}

        {props.shapeEnabled && props.shapes && props.shapes.length > 1 ? (
          <ShapeSection
            shapes={props.shapes || []}
            shape={shape}
            translations={{
              shapeLabel: translations.shapeLabel,
              selectYourShape: translations.selectYourShape,
              shapes: props.allTranslations.Shapes,
            }}
            onChangeComplete={(shape) => {
              setShape(shape);
              setChanged((prev) => {
                return {
                  ...prev,
                  shape,
                };
              });
            }}
            onOpenShapes={() => {
              setView("shapes");
            }}
          />
        ) : null}

        {props.quantityEnabled ? (
          <PricesSection
            translations={{
              enterSize: translations.enterSize,
              customQuantityLabel: translations.customQuantityLabel,
              quantitySubtitle: t("quantitySubtitle"),

              incTax: translations.incTax,
              exTax: translations.exTax,
              save: translations.save,
              quantityTitle: translations.quantityTitle,
              quantityLabel: translations.quantityLabel,
              quantityHelperText: translations.quantityHelperText,
            }}
            prices={props.prices}
            pricesIncTax={props.pricesIncTax}
            handleCustomPricing={handleCustomPricing}
            handleCustomQuantity={handleCustomQuantity}
            moq={props.moq}
            quantity={props.quantity}
            locale={props.locale}
            price={props.price}
            quantityError={quantityError}
          />
        ) : null}
      </Box>

      <Footer
        graphicMode={props.graphicMode}
        loading={false}
        currencyCode={props.currencyCode}
        locale={props.locale}
        quantity={quantityNum}
        handleSubmit={props.onSubmit}
        moq={props.moq}
        price={props.price}
        translations={{
          continue: translations.continue,
          customize: translations.customize,
          addToCart: translations.addToCart,
          uploadText: translations.uploadText,
        }}
      />
    </>
  );
};
